// src/utils/generateInvoicePDF.js
// Browser-side invoice PDF generator.
// Uses pdf-lib (dynamic import). Takes supabaseClient as param — never imports supabase directly.

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 40;
const COL_W  = PAGE_W - MARGIN * 2; // 532

// Table column layout (total = COL_W = 532)
const TABLE_DESC_X  = MARGIN;        // description left edge, width ~270
const TABLE_QTY_X   = 310;           // qty left edge, width 50, centered
const TABLE_UNIT_R  = 470;           // unit price right edge (col width 110)
const TABLE_RIGHT   = MARGIN + COL_W; // 572 — rightmost edge

const ROW_H = 18;

// ── Utilities ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${fmtDate(iso)} ${hh}:${mm}`;
}

function fmt2(n) {
  const v = parseFloat(n) || 0;
  return `$${Math.abs(v).toFixed(2)}`;
}

function shortBoxId(boxId) {
  if (!boxId) return '—';
  const parts = String(boxId).split('-');
  return parts[parts.length - 1] || boxId;
}

function truncateText(text, font, size, maxWidth) {
  let t = String(text || '');
  while (t.length > 4 && font.widthOfTextAtSize(t, size) > maxWidth) {
    t = t.slice(0, -1);
  }
  return t;
}

// Word-wraps text to maxWidth, returning at most maxLines lines.
// The final line is ellipsised if content remains.
function wrapText(text, font, size, maxWidth, maxLines = 2) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (font.widthOfTextAtSize(test, size) <= maxWidth) {
      cur = test;
    } else {
      if (cur) lines.push(cur);
      cur = w;
      if (lines.length === maxLines) break;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines) {
    const consumed = lines.join(' ').split(/\s+/).length;
    if (consumed < words.length) {
      lines[maxLines - 1] = truncateText(lines[maxLines - 1] + '…', font, size, maxWidth);
    }
  }
  return lines;
}

const PAYMENT_METHOD_LABELS = {
  'us_bank':        'Banco USA',
  'banco_agricola': 'Banco Agrícola',
  'zelle':          'Zelle',
  'wise':           'Wise',
  'cash':           'Efectivo',
  'other':          'Otro',
};

const STATUS_MAP = {
  'draft':   { label: 'BORRADOR', r: 0.92, g: 0.60, b: 0.13 },
  'sent':    { label: 'ENVIADA',  r: 0.09, g: 0.35, b: 0.87 },
  'partial': { label: 'PARCIAL',  r: 0.53, g: 0.77, b: 0.22 },
  'paid':    { label: 'PAGADA',   r: 0.09, g: 0.57, b: 0.27 },
  'void':    { label: 'ANULADA',  r: 0.50, g: 0.50, b: 0.50 },
};

// ── Entry point ───────────────────────────────────────────────────────────────

export async function generateInvoicePDF(
  invoiceId,
  supabaseClient,
  { returnBytes = false } = {}
) {
  // ── 1. FETCH DATA ──────────────────────────────────────────────────────────

  const [invRes, linesRes, paymentsRes] = await Promise.all([
    supabaseClient.from('invoices').select('*').eq('id', invoiceId).maybeSingle(),
    supabaseClient.from('invoice_line_items').select('*').eq('invoice_id', invoiceId).order('sort_order'),
    supabaseClient.from('invoice_payments').select('*').eq('invoice_id', invoiceId).order('paid_at'),
  ]);

  if (invRes.error)  throw new Error(`Error al cargar factura: ${invRes.error.message}`);
  if (!invRes.data)  throw new Error('Factura no encontrada.');
  const inv = invRes.data;

  const [custRes, tenantRes] = await Promise.all([
    supabaseClient.from('customers').select('id, full_name, phone_number').eq('id', inv.customer_id).maybeSingle(),
    supabaseClient.from('tenants').select('company_name, logo_url, office_address, office_phone').eq('id', inv.tenant_id).maybeSingle(),
  ]);

  if (custRes.error)    throw new Error(`Error al cargar cliente: ${custRes.error.message}`);
  if (tenantRes.error)  throw new Error(`Error al cargar empresa: ${tenantRes.error.message}`);
  if (!custRes.data)    throw new Error('Cliente no encontrado.');
  if (!tenantRes.data)  throw new Error('Empresa no encontrada.');

  const customer  = custRes.data;
  const tenant    = tenantRes.data;
  const lineItems = linesRes.data    || [];
  const payments  = paymentsRes.data || [];

  // Fetch article data from declaration_drafts and box_packages (best-effort — never throws)
  let ddRow = null;
  let bpRows = [];
  try {
    const [ddResult, bpResult] = await Promise.all([
      supabaseClient.from('declaration_drafts').select('payload').eq('manifest_id', inv.manifest_id).maybeSingle(),
      supabaseClient.from('box_packages').select('package_ref_id, package_type').eq('box_id', inv.box_id).eq('client_id', inv.customer_id).eq('tenant_id', inv.tenant_id),
    ]);
    ddRow  = ddResult.data  || null;
    bpRows = bpResult.data  || [];
  } catch (e) {
    console.warn('[InvoicePDF] article data fetch failed:', e?.message);
  }

  // Destinatario (ES delivery recipient) — best effort, never throws.
  let boxRow = null;
  try {
    if (inv.box_id) {
      const { data } = await supabaseClient
        .from('boxes')
        .select('recipient_name, recipient_phone, recipient_address, municipio, departamento')
        .eq('box_id', inv.box_id)
        .eq('tenant_id', inv.tenant_id)
        .maybeSingle();
      boxRow = data || null;
    }
  } catch (e) {
    console.warn('[InvoicePDF] destinatario fetch failed:', e?.message);
  }

  const expectedDeckKeys = new Set(
    (bpRows || []).map(bp =>
      bp.package_type === 'encomienda' ? `enc-${bp.package_ref_id}` :
      bp.package_type === 'casillero'  ? `ship-${bp.package_ref_id}` :
      null
    ).filter(Boolean)
  );

  const customerPackages = (ddRow?.payload?.packages || [])
    .filter(p => expectedDeckKeys.has(p.deck_key));

  const articleItems = customerPackages
    .flatMap(p => (p.items || []).map(it => ({
      quantity:    parseInt(it.quantity, 10) || 1,
      description: it.description || it.product_name || 'Sin descripción',
      unit_value:  parseFloat(it.unit_value) || 0,
      line_total:  ((parseInt(it.quantity, 10) || 1) * (parseFloat(it.unit_value) || 0)),
    })));

  // Split line items for table vs. totals block
  const tableLines      = lineItems.filter(l => !['dai', 'iva'].includes(l.line_type));
  const daiLines        = lineItems.filter(l => l.line_type === 'dai');
  const ivaLines        = lineItems.filter(l => l.line_type === 'iva');
  const adjustmentLines = lineItems.filter(l => l.line_type === 'adjustment');
  const discountLines   = lineItems.filter(l => l.line_type === 'discount');
  const freightLines    = lineItems.filter(l => l.line_type?.startsWith('freight_'));
  const surchargeLines  = lineItems.filter(l => l.line_type?.startsWith('surcharge_'));

  const daiTotal  = daiLines.reduce((s, l)        => s + (l.line_total || 0), 0);
  const ivaTotal  = ivaLines.reduce((s, l)        => s + (l.line_total || 0), 0);
  const adjTotal  = adjustmentLines.reduce((s, l) => s + (l.line_total || 0), 0);
  const discTotal = discountLines.reduce((s, l)   => s + (l.line_total || 0), 0);

  // Breakdown rows derive from line items, never from the stored invoice columns.
  const subtotalTotal  = freightLines.reduce((s, l)   => s + (Number(l.line_total) || 0), 0);
  const surchargeTotal = surchargeLines.reduce((s, l) => s + (Number(l.line_total) || 0), 0);

  // ── 2. pdf-lib SETUP ───────────────────────────────────────────────────────

  const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
  const pdfDoc   = await PDFDocument.create();
  const font     = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontItal = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const black     = rgb(0,    0,    0);
  const white     = rgb(1,    1,    1);
  const muted     = rgb(0.45, 0.45, 0.45);
  const lightGray = rgb(0.88, 0.88, 0.88);
  const blue      = rgb(0.09, 0.35, 0.87);
  const red       = rgb(0.80, 0.20, 0.20);

  // ── 3. LOGO FETCH ──────────────────────────────────────────────────────────

  let logoImage = null;
  if (tenant.logo_url) {
    try {
      const resp = await fetch(tenant.logo_url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const logoBytes = await resp.arrayBuffer();
      try {
        logoImage = await pdfDoc.embedPng(logoBytes);
      } catch {
        logoImage = await pdfDoc.embedJpg(logoBytes);
      }
    } catch (err) {
      console.warn('[InvoicePDF] Logo no disponible:', err?.message);
    }
  } else {
    console.warn('[InvoicePDF] tenant.logo_url vacío — PDF generado sin logo');
  }

  // ── 4. DRAWING HELPERS ─────────────────────────────────────────────────────

  const newPage = () => pdfDoc.addPage([PAGE_W, PAGE_H]);

  function drawHRule(pg, y, color = lightGray, thickness = 0.5) {
    pg.drawLine({
      start: { x: MARGIN, y },
      end:   { x: TABLE_RIGHT, y },
      thickness,
      color,
    });
  }

  function drawShortRule(pg, y) {
    pg.drawLine({
      start: { x: TABLE_RIGHT - 200, y },
      end:   { x: TABLE_RIGHT, y },
      thickness: 0.8,
      color: muted,
    });
  }

  function drawRight(pg, text, rightX, y, size, f, color) {
    const w = f.widthOfTextAtSize(String(text), size);
    pg.drawText(String(text), { x: rightX - w, y, size, font: f, color });
  }

  function drawCenter(pg, text, leftX, width, y, size, f, color) {
    const w = f.widthOfTextAtSize(String(text), size);
    pg.drawText(String(text), { x: leftX + (width - w) / 2, y, size, font: f, color });
  }

  // ── 5. FIRST PAGE ──────────────────────────────────────────────────────────

  let page = newPage();
  let curY = PAGE_H - MARGIN; // 752 (top minus margin, pdf-lib y=0 is bottom)

  // ── A. HEADER BAND ─────────────────────────────────────────────────────────

  const headerTopY = curY; // 752

  // Logo — top-left
  if (logoImage) {
    const maxW = 140, maxH = 60;
    const { width: iW, height: iH } = logoImage;
    const scale = Math.min(maxW / iW, maxH / iH);
    const dW = iW * scale;
    const dH = iH * scale;
    page.drawImage(logoImage, { x: MARGIN, y: headerTopY - dH, width: dW, height: dH });
  } else {
    // Fall back to company name text if no logo
    page.drawText(tenant.company_name || '', {
      x: MARGIN, y: headerTopY - 18,
      size: 13, font: fontBold, color: black,
    });
  }

  // Right column — "FACTURA" label, invoice number, date
  const dateStr = fmtDate(inv.issued_at || inv.created_at);
  drawRight(page, 'FACTURA',                  TABLE_RIGHT, headerTopY - 22,  24, fontBold, blue);
  drawRight(page, inv.invoice_number || '—',  TABLE_RIGHT, headerTopY - 42,  14, font,     black);
  drawRight(page, `Fecha: ${dateStr}`,         TABLE_RIGHT, headerTopY - 58,  10, font,     muted);

  curY = headerTopY - 95;
  drawHRule(page, curY);
  curY -= 14;

  // ── B. PARTY / OPERATIONAL BLOCK ───────────────────────────────────────────

  const partyStartY = curY;
  const colMidX     = MARGIN + COL_W / 2 + 10; // ~286

  // Left: bill-to
  page.drawText('NOMBRE CLIENTE:', {
    x: MARGIN, y: partyStartY, size: 9, font: fontBold, color: muted,
  });
  page.drawText(customer.full_name || '—', {
    x: MARGIN, y: partyStartY - 14, size: 12, font: fontBold, color: black,
  });
  page.drawText(`ID: ${customer.id}`, {
    x: MARGIN, y: partyStartY - 28, size: 10, font, color: black,
  });
  if (customer.phone_number) {
    page.drawText(`Tel: ${customer.phone_number}`, {
      x: MARGIN, y: partyStartY - 40, size: 10, font, color: black,
    });
  }

  // Right: shipment details
  page.drawText('DETALLES DEL ENVÍO:', {
    x: colMidX, y: partyStartY, size: 9, font: fontBold, color: muted,
  });
  page.drawText(`Manifiesto: ${inv.manifest_id || '—'}`, {
    x: colMidX, y: partyStartY - 14, size: 10, font, color: black,
  });
  page.drawText(`Caja: ${shortBoxId(inv.box_id)}`, {
    x: colMidX, y: partyStartY - 26, size: 10, font, color: black,
  });
  page.drawText(`Peso: ${(parseFloat(inv.total_weight_lbs) || 0).toFixed(1)} lb`, {
    x: colMidX, y: partyStartY - 38, size: 10, font, color: black,
  });

  // Destinatario block — left column, beneath the client's phone.
  let leftY = partyStartY - 40;
  if (boxRow && (boxRow.recipient_name || boxRow.recipient_address)) {
    const LEFT_MAX_W = colMidX - MARGIN - 12;
    leftY -= 16;
    page.drawText('DESTINATARIO:', {
      x: MARGIN, y: leftY, size: 9, font: fontBold, color: muted,
    });
    if (boxRow.recipient_name) {
      leftY -= 12;
      page.drawText(truncateText(boxRow.recipient_name.trim(), font, 10, LEFT_MAX_W), {
        x: MARGIN, y: leftY, size: 10, font, color: black,
      });
    }
    // Only append municipio/departamento when the free-text address doesn't
    // already end with them — operators frequently type both into the address.
    const addrRaw = String(boxRow.recipient_address || '').trim();
    const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
    const geoParts = [boxRow.municipio, boxRow.departamento].filter(Boolean);
    const tail = norm(addrRaw.split(',').slice(-geoParts.length).join(''));
    const geo = (geoParts.length > 0 && tail === norm(geoParts.join('')))
      ? ''
      : geoParts.join(', ');
    const addrFull = [addrRaw, geo].filter(Boolean).join(' — ');
    for (const ln of wrapText(addrFull, font, 9, LEFT_MAX_W, 2)) {
      leftY -= 11;
      page.drawText(ln, { x: MARGIN, y: leftY, size: 9, font, color: black });
    }
    if (boxRow.recipient_phone) {
      leftY -= 11;
      page.drawText(`Tel destinatario: ${boxRow.recipient_phone}`, {
        x: MARGIN, y: leftY, size: 9, font, color: muted,
      });
    }
  }

  // Rule sits below whichever column is taller: left block or the 3-line right block.
  curY = Math.min(leftY - 16, partyStartY - 56);
  drawHRule(page, curY);
  curY -= 16;

  // ── C. LINE ITEMS TABLE ────────────────────────────────────────────────────

  // Column headers
  page.drawText('DESCRIPCIÓN', {
    x: TABLE_DESC_X, y: curY, size: 9, font: fontBold, color: muted,
  });
  drawCenter(page, 'CANT.',        TABLE_QTY_X,  50,  curY, 9, fontBold, muted);
  drawRight(page,  'PRECIO UNIT.', TABLE_UNIT_R,      curY, 9, fontBold, muted);
  drawRight(page,  'TOTAL',        TABLE_RIGHT,        curY, 9, fontBold, muted);

  curY -= 5;
  drawHRule(page, curY);
  curY -= ROW_H;

  for (const li of tableLines) {
    // Overflow guard: keep at least 160pt above margin for totals block
    if (curY < MARGIN + 160) {
      page = newPage();
      curY = PAGE_H - MARGIN;
    }

    // Description — truncate to fit column width (~265pt)
    let desc = String(li.description || '');
    if (li.line_type === 'discount' && !desc.toLowerCase().includes('descuento')) {
      desc += ' (Descuento)';
    }
    desc = truncateText(desc, font, 10, 265);

    page.drawText(desc, { x: TABLE_DESC_X, y: curY, size: 10, font, color: black });

    // Qty — centered in 50pt slot
    const qty    = li.quantity ?? 1;
    const qtyStr = Number.isInteger(qty) ? String(qty) : parseFloat(qty).toFixed(1);
    drawCenter(page, qtyStr, TABLE_QTY_X, 50, curY, 10, font, black);

    // Unit price and total
    const unitStr  = fmt2(li.unit_price);
    const isDisc   = li.line_type === 'discount';
    const totalAmt = isDisc ? -Math.abs(li.line_total || 0) : (li.line_total || 0);
    const totalStr = isDisc ? `-${fmt2(Math.abs(totalAmt))}` : fmt2(totalAmt);

    drawRight(page, unitStr,  TABLE_UNIT_R, curY, 10, font, black);
    drawRight(page, totalStr, TABLE_RIGHT,  curY, 10, font, isDisc ? red : black);

    curY -= ROW_H;
  }

  drawHRule(page, curY + 4);
  curY -= 12;

  // ── D. TOTALS BLOCK (right-aligned) ────────────────────────────────────────

  const totalsLabelX = TABLE_RIGHT - 200;

  const totalRows = [
    { label: 'Subtotal fletes:', val: subtotalTotal,   show: true },
    { label: 'Recargo:',         val: surchargeTotal,  show: surchargeTotal > 0 },
    { label: 'DAI:',             val: daiTotal,        show: daiTotal > 0 },
    { label: 'IVA:',             val: ivaTotal,        show: ivaTotal > 0 },
    // Issued without a declaration: no amount — the tax goes on a later invoice.
    { label: 'Impuesto: pendiente de declaración', val: null, show: !!inv.impuesto_pendiente, noAmount: true },
    { label: 'Recargos:',       val: adjTotal,        show: adjTotal > 0 },
    { label: 'Descuentos:',      val: discTotal,       show: discTotal < 0 },
  ].filter(r => r.show);

  for (const row of totalRows) {
    const valStr  = row.label === 'Descuentos:' ? `-${fmt2(Math.abs(row.val))}` : fmt2(row.val);
    const valColor = row.label === 'Descuentos:' ? red : black;
    page.drawText(row.label, {
      x: totalsLabelX, y: curY, size: 10, font, color: muted,
    });
    if (!row.noAmount) drawRight(page, valStr, TABLE_RIGHT, curY, 10, font, valColor);
    curY -= 16;
  }

  drawShortRule(page, curY + 4);
  curY -= 18;

  // TOTAL row — summed from line items so the total can never contradict the
  // breakdown above it, even if the stored invoices.total column drifts.
  const computedTotal = (linesRes.data || []).reduce(
    (s, l) => s + (Number(l.line_total) || 0),
    0
  );

  // Smoke alarm: reconciliation in FacturacionPanel should keep these in step.
  if (Math.abs(computedTotal - (Number(inv.total) || 0)) > 0.01) {
    console.warn('[generateInvoicePDF] total drift detected:', {
      invoiceId,
      invoice_number: inv.invoice_number,
      computedTotal,
      storedTotal: inv.total,
      drift: computedTotal - (Number(inv.total) || 0),
    });
  }

  page.drawText('TOTAL:', { x: totalsLabelX, y: curY, size: 14, font: fontBold, color: black });
  drawRight(page, fmt2(computedTotal), TABLE_RIGHT, curY, 14, fontBold, black);
  curY -= 24;

  // ── E. PAYMENT STATUS PILL ─────────────────────────────────────────────────

  const pillInfo  = STATUS_MAP[inv.status] || STATUS_MAP['draft'];
  const pillLabel = pillInfo.label;
  const pillColor = rgb(pillInfo.r, pillInfo.g, pillInfo.b);
  const pillPadX  = 10;
  const pillPadY  = 4;
  const pillTextW = fontBold.widthOfTextAtSize(pillLabel, 10);
  const pillW     = pillTextW + pillPadX * 2;
  const pillH     = 10 + pillPadY * 2;

  page.drawRectangle({
    x:      TABLE_RIGHT - pillW,
    y:      curY,
    width:  pillW,
    height: pillH,
    color:  pillColor,
  });
  page.drawText(pillLabel, {
    x:    TABLE_RIGHT - pillW + pillPadX,
    y:    curY + pillPadY + 1,
    size: 10, font: fontBold, color: white,
  });
  curY -= 30;

  // ── H. ARTICLE BREAKDOWN ───────────────────────────────────────────────────

  if (articleItems.length > 0) {
    curY -= 8;

    page.drawText('DETALLE DE ARTÍCULOS', {
      x: MARGIN, y: curY, size: 10, font: fontBold, color: muted,
    });
    curY -= 6;
    drawHRule(page, curY);
    curY -= 14;

    // Article table columns — MARGIN=40, COL_W=532: 50+322+80+80=532
    const ART_QTY_R   = MARGIN + 46;        // qty right edge  (x=86, 4pt gutter before desc)
    const ART_DESC_X  = MARGIN + 50;        // desc left edge  (x=90)
    const ART_UNIT_R  = ART_DESC_X + 402;  // unit right edge (x=492)
    const ART_TOTAL_R = TABLE_RIGHT;        // total right edge (x=572)

    page.drawText('CANT',        { x: MARGIN,     y: curY, size: 8, font: fontBold, color: muted });
    page.drawText('DESCRIPCIÓN', { x: ART_DESC_X, y: curY, size: 8, font: fontBold, color: muted });
    drawRight(page, 'VALOR UNIT.', ART_UNIT_R,  curY, 8, fontBold, muted);
    drawRight(page, 'TOTAL',       ART_TOTAL_R, curY, 8, fontBold, muted);
    curY -= 5;
    drawHRule(page, curY);
    curY -= 13;

    const ART_ROW_H = 13;

    for (const it of articleItems) {
      if (curY < MARGIN + 110) {
        page = newPage();
        curY = PAGE_H - MARGIN;
        page.drawText('CANT',        { x: MARGIN,     y: curY, size: 8, font: fontBold, color: muted });
        page.drawText('DESCRIPCIÓN', { x: ART_DESC_X, y: curY, size: 8, font: fontBold, color: muted });
        drawRight(page, 'VALOR UNIT.', ART_UNIT_R,  curY, 8, fontBold, muted);
        drawRight(page, 'TOTAL',       ART_TOTAL_R, curY, 8, fontBold, muted);
        curY -= 5;
        drawHRule(page, curY);
        curY -= ART_ROW_H;
      }

      const rawDesc = String(it.description || '');
      const desc    = rawDesc.length > 50 ? rawDesc.slice(0, 50) + '…' : rawDesc;

      drawRight(page, String(it.quantity),              ART_QTY_R,   curY, 9, font, black);
      page.drawText(desc, { x: ART_DESC_X, y: curY,   size: 9, font, color: black });
      drawRight(page, `$${it.unit_value.toFixed(2)}`,  ART_UNIT_R,  curY, 9, font, black);
      drawRight(page, `$${it.line_total.toFixed(2)}`,  ART_TOTAL_R, curY, 9, font, black);
      curY -= ART_ROW_H;
    }

    drawHRule(page, curY + 4);
    curY -= 8;
    const artTotal = articleItems.reduce((s, it) => s + it.line_total, 0);
    drawRight(page, `Total valor declarado: $${artTotal.toFixed(2)}`, TABLE_RIGHT, curY, 9, fontBold, black);
    curY -= 20;
  }

  // ── F. PAYMENTS DETAIL ─────────────────────────────────────────────────────

  if (payments.length > 0) {
    page.drawText('PAGOS RECIBIDOS:', {
      x: MARGIN, y: curY, size: 9, font: fontBold, color: muted,
    });
    curY -= 14;

    for (const pmt of payments) {
      if (curY < MARGIN + 110) {
        page = newPage();
        curY = PAGE_H - MARGIN;
      }
      const methodLabel = PAYMENT_METHOD_LABELS[pmt.payment_method] || pmt.payment_method || '—';
      const ref         = pmt.reference_number ? ` (${pmt.reference_number})` : '';
      const pmtStr      = `${fmtDate(pmt.paid_at)} — ${methodLabel} — ${fmt2(pmt.amount)}${ref}`;
      page.drawText(pmtStr, { x: MARGIN, y: curY, size: 9, font, color: black });
      curY -= 13;
    }

    const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    page.drawText(`Total pagado: ${fmt2(totalPaid)}`, {
      x: MARGIN, y: curY, size: 9, font: fontBold, color: black,
    });
    curY -= 20;
  }

  // ── G. FOOTER ──────────────────────────────────────────────────────────────

  const officeLines = [];
  if (tenant.office_address) officeLines.push(truncateText(tenant.office_address.trim(), font, 8, COL_W));
  if (tenant.office_phone)   officeLines.push(`Tel: ${tenant.office_phone.trim()}`);
  const footerRuleY = MARGIN + 32 + officeLines.length * 11;
  const footerTextY = MARGIN + 16;

  // Notes (above footer rule, if present)
  if (inv.notes) {
    const notesStr = truncateText(String(inv.notes), fontItal, 9, COL_W);
    page.drawText(notesStr, {
      x: MARGIN, y: footerRuleY + 14,
      size: 9, font: fontItal, color: muted,
    });
  }

  drawHRule(page, footerRuleY, lightGray, 0.4);

  let officeY = footerRuleY - 12;
  for (const ln of officeLines) {
    const w = font.widthOfTextAtSize(ln, 8);
    page.drawText(ln, {
      x: MARGIN + (COL_W - w) / 2, y: officeY, size: 8, font, color: muted,
    });
    officeY -= 11;
  }

  const footerStr = `${tenant.company_name || ''} — Generado el ${fmtDateTime(new Date().toISOString())}`;
  const footerW   = font.widthOfTextAtSize(footerStr, 8);
  page.drawText(footerStr, {
    x:    MARGIN + (COL_W - footerW) / 2,
    y:    footerTextY,
    size: 8, font, color: muted,
  });

  // ── 6. SAVE + OUTPUT ───────────────────────────────────────────────────────

  const pdfBytes = await pdfDoc.save();
  console.log('[InvoicePDF] Generated:', inv.invoice_number, '— payments:', payments.length, '— lines:', lineItems.length);

  if (returnBytes) {
    return pdfBytes;
  }

  const filename = `${inv.invoice_number || invoiceId}.pdf`;
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
